import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { login } from 'actions/user';
import { SaveButton, StringHelpers } from '@salesperformanceint/e4shared';
import { apiGet, apiPost } from 'services';
import config from 'config';

export const Login = ({ history }) => {
    const dispatch = useDispatch();

    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [error, setError] = React.useState('');
    const [url, setUrl] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [stage, setStage] = React.useState('EMAIL');

    const emailField = React.useRef(null);

    const { user, redirectPath, errorMessage } = useSelector(state => state.user);

    const checkSubmit = (e, callback) => {
        if (e.key === 'Enter') {
            callback();
        }
    };

    React.useEffect(() => {
        emailField?.current && emailField?.current.focus();
    }, [emailField?.current]);

    React.useEffect(() => {
        setError(errorMessage);
    }, [errorMessage]);

    const doLogin = () => {
        if (stage === 'EMAIL') {
            if (!email) {
                setError('Please supply an email address');
                setSaving(false);
            } else if (!StringHelpers.matchRegEx(StringHelpers.regExs.EMAIL, email)) {
                setError('Please supply a valid email address');
                setSaving(false);
            } else {
                //window.location.href = `https://login.microsoftonline.com/4120b077-e8cd-4fd0-98f0-16333d257b22/saml2?SAMLRequest=fFFLT8MwGPsrUe5Zmz7WLVqLxiZEpQHVVjhwQWmbbhFpUpKUx78n64YEB3a1bH%2b2v4WhnejJcrAHuWVvAzMW5OsUvswTVoXVLEZxO21RVLUBmkVRi5I4mEcxDqtojiF4YtpwJVMYTHwIcmMGlktjqbQO8oMY%2bRjhsMQhiaYEx88QFFpZVStxzWXD5T6Fg5ZEUcMNkbRjhtia7JZ3G%2bIcSXUiGXJblgUqHnYlBEtjmLbu6EpJM3RM75h%2b5zV73G5SeLC2J54nVE3FQRlLEj%2fBHu25g%2fZcese2XnBVq4alEHx2QhoyLnA5R38ODbPFkU3GovqX%2frKc%2fkSGmeT1K7Ju5cWY5ex0su3JvZPm60IJXn%2bBG6U76nb8zxlP8IjwBrUjlQzS9KzmLWeN20kI9bHSjFpX1eqBQeBlp6t%2f%2f519AwAA%2f%2f8DAA%3d%3d`;
                apiPost(
                    'login-samlemailcheck',
                    {
                        email,
                        redirectPath,
                        apiUrl: `${config.apiGateway.URL}login/saml/COMPANY_ID?code=${config.apiGateway.CODE}`,
                    },
                    '',
                    response => {
                        setError('');
                        setSaving(false);

                        if (response.redirect) {
                            window.location.href = response.url;
                        } else {
                            setUrl(response.url);
                            setStage('PASSWORD');
                        }
                    },
                    () => {
                        setError('');
                        setSaving(false);
                        setStage('PASSWORD');
                    },
                    'POST',
                    'json'
                );
            }
        } else {
            setSaving(false);
            dispatch(login(email, password));
        }
    };

    if (user) {
        if (redirectPath) {
            history.push(redirectPath);
        } else {
            history.push('/');
        }
    }

    return (
        <div className="login-box">
            <div className="login-logo">
                <img alt="Richardson Sales Performance" src="/dist/img/richardson_sp.png" loading="lazy" />
            </div>
            <div className="login-body">
                <p>Sign in to start your session</p>
                {error && (
                    <p className="error">
                        <i className="fa fa-solid fa-exclamation-circle text-danger mr5" />
                        {error}
                    </p>
                )}
                <div className="mb20 has-feedback">
                    <input
                        autoComplete="username"
                        id="username"
                        ref={emailField}
                        onChange={e => setEmail(e.target.value)}
                        onKeyUp={e => checkSubmit(e, doLogin)}
                        type="email"
                        disabled={stage === 'PASSWORD'}
                        className="form-control"
                        placeholder="Email Address"
                        data-testid="text-email"
                    />
                    <span className="glyphicon glyphicon-envelope form-control-feedback" />
                </div>
                {stage === 'PASSWORD' && (
                    <div className="mb20 has-feedback">
                        <input
                            id="password"
                            onChange={e => setPassword(e.target.value)}
                            onKeyUp={e => checkSubmit(e, doLogin)}
                            type="password"
                            className="form-control"
                            placeholder="Password"
                            data-testid="text-password"
                        />
                        <span className="glyphicon glyphicon-lock form-control-feedback" />
                    </div>
                )}
                <div className="flex flex-center no-wrap login-below">
                    <div className="flex-grow">
                        <SaveButton
                            saving={saving}
                            setSaving={setSaving}
                            onClick={() => doLogin()}
                            onKeyUp={e => checkSubmit(e, doLogin)}
                            className="btn btn-primary"
                            data-testid="button-signin"
                        >
                            {stage === 'EMAIL' ? 'Continue' : 'Sign In'}
                        </SaveButton>
                    </div>
                    {stage === 'PASSWORD' && (
                        <Link to="/forgot-password" className="ml10" data-testid="link-reset-password">
                            Reset my password
                        </Link>
                    )}
                </div>
                {url && stage === 'PASSWORD' && (
                    <div className="mt50 text-left">
                        <div>Single Sign On Options</div>
                        <hr className="flex-grow" />
                        {url.toLowerCase().indexOf('microsoft') >= 0 ? (
                            <a
                                href={url}
                                className="sss-azure btn-block mt10"
                                data-testid="link-azure-sso"
                                title="Login via Azure"
                            >
                                <img src="/dist/img/sso-azure.svg" alt="" loading="lazy" />
                            </a>
                        ) : (
                            <a
                                href={url}
                                className="btn btn-primary mt10"
                                data-testid="link-generic-sso"
                                title="Login via your SAML provider"
                            >
                                Login using Single Sign On
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Login;
